<template>
  <div class="drawer-container">
    <transition name="fade">
      <div
        v-if="isOpen"
        class="backdrop"
        :style="{ zIndex: zIndex - 1 }"
        @click="isOpen = false"
      ></div>
    </transition>

    <transition name="slide-right">
      <div
        v-if="isOpen"
        :class="['right-drawer', padded ? 'drawer-padded' : '']"
        :style="drawerStyle"
      >
        <q-btn
          v-show="showClose"
          icon="fa fa-times"
          round
          flat
          @click="close"
          style="position: absolute; top: 0; right: 0"
        />
        <div :class="['drawer-content', padded ? 'drawer-padded' : '']">
          <!-- {{ {props, showClose, padded} }} -->
          <component
            v-if="component"
            :is="component"
            v-bind="{ ...props, }"
            v-on="allHandlers"
          />
        </div>
      </div>
    </transition>
  </div>
</template>


<style scoped>
/* --- Layout Styles --- */
.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  /* z-index set dynamically via :style, just below the drawer panel */
}

.right-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  /* Full Height */
  background-color: white;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  /* z-index set dynamically via :style */
}

.drawer-header {
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.drawer-padded {
  padding: 20px;
}

.drawer-content {
  /* padding: 20px; */
  flex-grow: 1;
  overflow-y: auto;
  /* Scroll content if it gets too long */
}

/* --- Transitions --- */

/* 1. Backdrop Fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 2. Drawer Slide */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease-in-out;
}

/* State when hidden:
   translateX(100%) moves the drawer to the right,
   completely off-screen.
*/
.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}
</style>


<script lang="ts">
import { defineComponent, Component } from 'vue';
import { layerStack } from './layer-stack';
import { layoutBus, OpenDrawerCommand } from '.';

interface Data {
  isOpen: boolean;
  zIndex: number;
  component: Component | null;
  props: Record<string, any>;
  handlers: Record<string, (...args: any[]) => void>;
  closable?: boolean;
  paddless?: boolean;
  width?: string;
}

export default defineComponent({
  data(): Data {
    return {
      isOpen: false,
      zIndex: 0,
      component: null,
      props: {},
      handlers: {},
      closable: true,
      paddless: false,
      width: '' as string,
    };
  },
  computed: {
    allHandlers(): Record<string, (...args: any[]) => void> {
      return {
        ...this.handlers,
        close: () => {
          if (this.handlers.close) {
            this.handlers.close();
          }
          this.close();
        },
      };
    },
    showClose(): boolean {
      return this.isOpen && this.closable === true;
    },
    padded(): boolean {
      return !this.paddless;
    },
    drawerStyle(): Record<string, any> {
      const style: Record<string, any> = { zIndex: this.zIndex };
      if (this.width) {
        style.width = this.width;
      }
      return style;
    },
  },
  methods: {
    open(command: OpenDrawerCommand): void {
      this.zIndex = layerStack.push();
      this.isOpen = true;
      this.component = command.component;
      this.props = command.props || {};
      this.handlers = command.on || {};
      this.closable = 'closable' in command ? command.closable : true;
      this.paddless = 'paddless' in command ? command.paddless : false;
      this.width = command.width || '';
      document.addEventListener('keydown', this.handleEscKey);
    },
    close(): void {
      this.isOpen = false;
      layerStack.pop();
      document.removeEventListener('keydown', this.handleEscKey);
    },
    handleEscKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && this.isOpen && this.closable && this.zIndex === layerStack.peek()) {
        this.close();
      }
    },
  },
  created(): void {
    layoutBus.$on('drawer.open', this.open);
    layoutBus.$on('drawer.close', this.close);
  },
  beforeUnmount(): void {
    layoutBus.$off('drawer.open', this.open);
    layoutBus.$off('drawer.close', this.close);
    if (this.isOpen) {
      layerStack.pop();
      document.removeEventListener('keydown', this.handleEscKey);
    }
  },
});
</script>
